import { useSelector } from "react-redux";

import { useRbacPrimaryRolePermissions } from "../../api/hooks";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

import { getCompanySettingsContext, settingsHaveBeenFetched } from "../../selectors/settings";

export type WorkflowsPageGate = {
	/** True while any of the flag, RBAC or settings inputs are still resolving. */
	isGateLoading: boolean;
	featureAvailable: boolean;
	canView: boolean;
	canManage: boolean;
	/** Document signing is separately gated; the to-do form hides its signature tab without it. */
	docSigningEnabled: boolean;
};

/**
 * The feature gate shared by the three Workflows pages.
 *
 * All of them are behind the same three inputs — the `ff-is-workflows-enabled` flag,
 * the company's `workflows` setting, and the `workflows` primary-role permissions —
 * so they resolve it here rather than each re-deriving it and risking drift.
 */
const useWorkflowsPageGate = (): WorkflowsPageGate => {
	const {
		value: workflowsFeatureVisible,
		query: { isLoading: isLoadingWorkflowsFF },
	} = useFeatureFlagQuery("ff-is-workflows-enabled", true);
	const { value: docSigningFeatureVisible } = useFeatureFlagQuery("ff-is-document-signing-enabled", true);
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("workflows");

	const companySettings = useSelector(getCompanySettingsContext);
	const haveSettingsBeenFetched = useSelector(settingsHaveBeenFetched);

	const workflowsEnabled = companySettings.getIn(["workflows", "state"], "disabled") === "enabled";
	const canBypassFeatureGate = !!rbacPermissions?.includes("workflows:bypassFeatureGate");

	return {
		isGateLoading: isLoadingWorkflowsFF || isRbacLoading || !haveSettingsBeenFetched,
		featureAvailable: canBypassFeatureGate || (workflowsFeatureVisible && workflowsEnabled),
		canView: !!rbacPermissions?.includes("workflows:viewAdmin"),
		canManage: !!rbacPermissions?.includes("workflows:manageAdmin"),
		docSigningEnabled:
			companySettings.getIn(["documentSigning", "state"], "disabled") === "enabled" && docSigningFeatureVisible,
	};
};

export default useWorkflowsPageGate;
